<template>
  <div class="recon-profitability">
    <EntraPageHeader
      title="Recon Profitability"
      :showTenantInfo="false"
      :showBreadcrumb="false"
      :showSearch="false"
      :showSavedViews="false"
      :showExport="false"
      :showRefresh="true"
      @refresh="loadAll"
    />
    <div class="page-body">
      <div v-if="error" class="error-message">{{ error }}</div>

      <div class="card">
        <div class="overview-filters">
          <div class="filter-group">
            <label class="filter-label">Period:</label>
            <select v-model="periodType" class="filter-select" @change="onPeriodTypeChange">
              <option value="last3">Last 3 billing months</option>
              <option value="last6">Last 6 billing months</option>
              <option value="last12">Last 12 billing months</option>
              <option value="month">Single month</option>
              <option value="quarter">Quarter</option>
              <option value="year">Calendar year</option>
              <option value="all">All time</option>
            </select>
          </div>
          <div class="filter-group" v-if="periodType === 'month'">
            <label class="filter-label">Month:</label>
            <select v-model="selectedMonth" class="filter-select" @change="loadAll">
              <option v-if="monthOptions.length === 0" value="" disabled>No data available</option>
              <option v-for="opt in monthOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </div>
          <div class="filter-group" v-if="periodType === 'quarter'">
            <label class="filter-label">Quarter:</label>
            <select v-model="selectedQuarter" class="filter-select" @change="loadAll">
              <option v-if="quarterOptions.length === 0" value="" disabled>No data available</option>
              <option v-for="opt in quarterOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </div>
          <div class="filter-group" v-if="periodType === 'year'">
            <label class="filter-label">Year:</label>
            <select v-model="selectedYear" class="filter-select" @change="loadAll">
              <option v-if="yearOptions.length === 0" value="" disabled>No data available</option>
              <option v-for="opt in yearOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label" id="recon-type-filter-label">Recon Type:</label>
            <div class="type-tabs" role="tablist" aria-labelledby="recon-type-filter-label">
              <button
                v-for="tab in typeTabs"
                :key="tab.value || 'both'"
                type="button"
                role="tab"
                :aria-selected="(reconType === tab.value).toString()"
                class="type-tab"
                :class="{ 'type-tab-active': reconType === tab.value }"
                @click="onTypeChange(tab.value)"
              >{{ tab.label }}</button>
            </div>
          </div>
          <div class="filter-group">
            <label class="filter-label">Client filter:</label>
            <input v-model="search" class="filter-input" placeholder="Name contains..." @keyup.enter="loadAll" />
          </div>
          <div class="filter-group">
            <button class="apply-btn" @click="loadAll">Apply</button>
          </div>
          <div class="filter-group" v-if="coveringLabel">
            <label class="filter-label">Showing:</label>
            <span class="window-label">{{ coveringLabel }}</span>
          </div>
        </div>
      </div>

      <div v-if="state === 'loading'" class="loading-state"><span>Loading...</span></div>

      <template v-else>
        <div v-if="reconType !== ''" class="summary-cards">
          <MetricCard label="Microsoft Cost" :value="formatCurrency(combined.costUsd, 'USD')" :state="state" />
          <MetricCard label="Client Charge" :value="formatCurrency(combined.chargeUsd, 'USD')" :state="state" />
          <MetricCard label="Margin" :value="formatCurrency(combined.marginUsd, 'USD')" :state="state" />
          <MetricCard label="Margin %" :value="formatPct(combined.marginPct)" :state="state" />
          <MetricCard label="Waived (margin leak)" :value="formatCurrency(combined.waivedUsd, 'USD')" :state="state" />
        </div>
        <div v-else-if="summaryColumns.length > 0" class="card type-summary">
          <div class="type-summary-grid">
            <div
              v-for="col in summaryColumns"
              :key="col.key"
              class="type-summary-col"
              :class="{ 'type-summary-combined': col.key === 'all' }"
            >
              <h4>{{ col.title }}</h4>
              <template v-if="col.data">
                <div class="summary-row"><span class="summary-name">Microsoft Cost</span><span class="summary-value">{{ formatCurrency(col.data.costUsd, 'USD') }}</span></div>
                <div class="summary-row"><span class="summary-name">Client Charge</span><span class="summary-value">{{ formatCurrency(col.data.chargeUsd, 'USD') }}</span></div>
                <div class="summary-row"><span class="summary-name">Margin</span><span class="summary-value" :style="marginStyle(col.data.marginPct)">{{ formatCurrency(col.data.marginUsd, 'USD') }}</span></div>
                <div class="summary-row"><span class="summary-name">Margin %</span><span class="summary-value" :style="marginStyle(col.data.marginPct)">{{ formatPct(col.data.marginPct) }}</span></div>
                <div class="summary-row"><span class="summary-name">Waived (margin leak)</span><span class="summary-value">{{ col.data.waivedUsd !== 0 ? formatCurrency(col.data.waivedUsd, 'USD') : '--' }}</span></div>
              </template>
              <p v-else class="type-no-data">No data in this window</p>
            </div>
          </div>
        </div>

        <div v-if="zeroChargeNotice" class="warn-message zero-charge-trigger" @click="toggleZeroCharge">
          {{ zeroChargeNotice }}
          <span class="zero-charge-toggle">{{ zeroChargeExpanded ? 'Hide detail ▲' : 'Show detail ▼' }}</span>
        </div>

        <div v-if="zeroChargeExpanded" class="card zero-charge-detail">
          <h3>Zero-charge billed lines — {{ coveringLabel }}</h3>
          <div v-if="zeroChargeState === 'loading'" class="loading-state"><span>Loading...</span></div>
          <div v-else-if="zeroChargeItems.length > 0" class="config-table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Product</th>
                  <th>SKU</th>
                  <th>Clean SKU</th>
                  <th>Month</th>
                  <th>Type</th>
                  <th style="text-align: right;">Lines</th>
                  <th style="text-align: right;">Qty</th>
                  <th style="text-align: right;">Cost (USD)</th>
                  <th>Hold Rule</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in zeroChargeItems" :key="idx">
                  <td>{{ item.customerName }}</td>
                  <td>{{ item.productName }}</td>
                  <td>{{ item.skuName }}</td>
                  <td>{{ item.cleanSku || '--' }}</td>
                  <td>{{ item.billingMonth }}</td>
                  <td>{{ item.reconType }}</td>
                  <td style="text-align: right;">{{ item.lineCount }}</td>
                  <td style="text-align: right;">{{ item.quantity }}</td>
                  <td style="text-align: right;">{{ formatCurrency(item.costUsd, 'USD') }}</td>
                  <td>{{ item.holdRule || '--' }}</td>
                  <td>{{ item.holdReason || '--' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="no-data">No zero-charge billed lines in this window.</p>
        </div>

        <div class="card" v-if="summary && summary.monthly.length > 0">
          <h3>Monthly Margin</h3>
          <table class="data-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Type</th>
                <th style="text-align: right;">Cost</th>
                <th style="text-align: right;">Charge</th>
                <th style="text-align: right;">Margin</th>
                <th style="text-align: right;">Margin %</th>
                <th style="text-align: right;">Waived</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(m, idx) in summary.monthly" :key="idx">
                <td>{{ m.billingMonth }}</td>
                <td>{{ m.reconType }}</td>
                <td style="text-align: right;">{{ formatCurrency(m.costUsd, 'USD') }}</td>
                <td style="text-align: right;">{{ formatCurrency(m.chargeUsd, 'USD') }}</td>
                <td style="text-align: right;" :style="marginStyle(m.marginPct)">{{ formatCurrency(m.marginUsd, 'USD') }}</td>
                <td style="text-align: right;" :style="marginStyle(m.marginPct)">{{ formatPct(m.marginPct) }}</td>
                <td style="text-align: right;">{{ formatCurrency(m.waivedUsd, 'USD') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="card">
          <h3>Profitability by Client</h3>
          <div v-if="clients.length > 0">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Type</th>
                  <th style="text-align: center;">Bill</th>
                  <th style="text-align: right;">Cost</th>
                  <th style="text-align: right;">Charge</th>
                  <th style="text-align: right;">Margin</th>
                  <th style="text-align: right;">Margin %</th>
                  <th style="text-align: right;">Markup %</th>
                  <th style="text-align: right;">Waived</th>
                  <th style="text-align: right;">Products</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in clients" :key="idx">
                  <td>{{ item.customerName }}</td>
                  <td>{{ item.reconType }}</td>
                  <td style="text-align: center;">{{ item.generateBill ? 'Yes' : 'No' }}</td>
                  <td style="text-align: right;">{{ formatCurrency(item.costUsd, 'USD') }}</td>
                  <td style="text-align: right;">{{ formatCurrency(item.chargeUsd, 'USD') }}</td>
                  <td style="text-align: right;" :style="marginStyle(item.marginPct)">{{ formatCurrency(item.marginUsd, 'USD') }}</td>
                  <td style="text-align: right;" :style="marginStyle(item.marginPct)">{{ formatPct(item.marginPct) }}</td>
                  <td style="text-align: right;" :style="marginStyle(item.marginPct)">{{ formatPct(item.markupPct) }}</td>
                  <td style="text-align: right;">{{ item.waivedUsd !== 0 ? formatCurrency(item.waivedUsd, 'USD') : '--' }}</td>
                  <td style="text-align: right;">{{ item.productCount }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="no-data">No client profitability data for this window.</p>
        </div>

        <div class="card">
          <h3>Markup by Product</h3>
          <div v-if="products.length > 0">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Type</th>
                  <th style="text-align: right;">Cost</th>
                  <th style="text-align: right;">Charge</th>
                  <th style="text-align: right;">Markup %</th>
                  <th style="text-align: right;">Margin %</th>
                  <th style="text-align: right;">Discount Rule</th>
                  <th style="text-align: right;">Waived</th>
                  <th style="text-align: right;">Clients</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in products" :key="idx">
                  <td>{{ item.productName }}</td>
                  <td>{{ item.reconType }}</td>
                  <td style="text-align: right;">{{ formatCurrency(item.costUsd, 'USD') }}</td>
                  <td style="text-align: right;">{{ formatCurrency(item.chargeUsd, 'USD') }}</td>
                  <td style="text-align: right;" :style="marginStyle(item.marginPct)">{{ formatPct(item.markupPct) }}</td>
                  <td style="text-align: right;" :style="marginStyle(item.marginPct)">{{ formatPct(item.marginPct) }}</td>
                  <td style="text-align: right;">{{ item.discountPct !== null ? (Number(item.discountPct) * 100).toFixed(1) + '%' : 'varies' }}</td>
                  <td style="text-align: right;">{{ item.waivedUsd !== 0 ? formatCurrency(item.waivedUsd, 'USD') : '--' }}</td>
                  <td style="text-align: right;">{{ item.customerCount }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="no-data">No product profitability data for this window.</p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import MetricCard from '@/components/entra/common/MetricCard.vue';
import {
  getReconProfitabilityPeriods,
  getReconProfitabilitySummary,
  getReconProfitabilityClients,
  getReconProfitabilityProducts,
  getReconZeroChargeLines
} from '@/services/billing-api.js';
import { formatCurrency } from '@/services/entra-utils.js';

var MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
var RELATIVE_WINDOWS = { last3: 3, last6: 6, last12: 12 };

function monthLabel(value) {
  var parts = String(value).split('-');
  var monthNumber = Number(parts[1]);
  if (!monthNumber || monthNumber < 1 || monthNumber > 12) return value;
  return MONTH_NAMES[monthNumber - 1] + ' ' + parts[0];
}

export default {
  name: 'ReconProfitability',
  components: {
    EntraPageHeader,
    MetricCard
  },
  data: function() {
    return {
      periodType: 'last6',
      selectedMonth: '',
      selectedQuarter: '',
      selectedYear: '',
      availableMonths: [],
      reconType: '',
      typeTabs: [
        { value: '', label: 'Both' },
        { value: 'M365', label: 'M365' },
        { value: 'Azure', label: 'Azure' }
      ],
      search: '',
      state: 'idle',
      error: '',
      summary: null,
      clients: [],
      products: [],
      zeroChargeExpanded: false,
      zeroChargeItems: [],
      zeroChargeState: 'idle'
    };
  },
  computed: {
    windowMonths: function() {
      return (this.summary && this.summary.windowMonths) || [];
    },
    monthOptions: function() {
      return this.availableMonths.slice().sort().reverse().map(function(value) {
        return { value: value, label: monthLabel(value) };
      });
    },
    quarterOptions: function() {
      var seen = {};
      var options = [];
      this.availableMonths.slice().sort().reverse().forEach(function(value) {
        var year = value.slice(0, 4);
        var quarter = Math.ceil(Number(value.slice(5, 7)) / 3);
        var key = year + '-Q' + quarter;
        if (seen[key]) return;
        seen[key] = true;
        options.push({ value: key, label: 'Q' + quarter + ' ' + year });
      });
      return options;
    },
    yearOptions: function() {
      var seen = {};
      return this.availableMonths.slice().sort().reverse().reduce(function(options, value) {
        var year = value.slice(0, 4);
        if (seen[year]) return options;
        seen[year] = true;
        options.push({ value: year, label: year });
        return options;
      }, []);
    },
    coveringLabel: function() {
      var months = this.windowMonths;
      if (months.length === 0) return '';
      var first = monthLabel(months[0]);
      var last = monthLabel(months[months.length - 1]);
      var range = first === last ? first : first + ' - ' + last;
      var label = range + ' · ' + months.length + (months.length === 1 ? ' billing month' : ' billing months') + ' · USD, net of VAT';
      var requested = RELATIVE_WINDOWS[this.periodType];
      if (requested && months.length < requested) {
        label += ' (only ' + months.length + (months.length === 1 ? ' month' : ' months') + ' of data so far)';
      }
      return label;
    },
    combined: function() {
      var totals = (this.summary && this.summary.totals) || [];
      var cost = 0, charge = 0, waived = 0;
      totals.forEach(function(t) {
        cost += Number(t.costUsd) || 0;
        charge += Number(t.chargeUsd) || 0;
        waived += Number(t.waivedUsd) || 0;
      });
      var margin = Math.round((charge - cost) * 100) / 100;
      return {
        costUsd: cost,
        chargeUsd: charge,
        waivedUsd: waived,
        marginUsd: margin,
        marginPct: charge > 0 ? Math.round((margin / charge) * 10000) / 100 : null
      };
    },
    summaryColumns: function() {
      if (!this.summary || this.state === 'loading') return [];
      var totals = this.summary.totals || [];
      function forType(reconType) {
        for (var i = 0; i < totals.length; i++) {
          if (totals[i].reconType === reconType) return totals[i];
        }
        return null;
      }
      return [
        { key: 'm365', title: 'M365', data: forType('M365') },
        { key: 'azure', title: 'Azure', data: forType('Azure') },
        { key: 'all', title: 'Combined', data: totals.length > 0 ? this.combined : null }
      ];
    },
    zeroChargeNotice: function() {
      var totals = (this.summary && this.summary.totals) || [];
      var notes = [];
      totals.forEach(function(t) {
        if (Number(t.zeroChargeCostUsd) > 0) {
          notes.push(t.reconType + ': ' + t.zeroChargeLineCount + ' billed lines (' + formatCurrency(t.zeroChargeCostUsd, 'USD') + ' cost) have zero client charge');
        }
      });
      return notes.join(' · ');
    }
  },
  mounted: function() {
    var self = this;
    this.loadPeriods().then(function() {
      self.loadAll();
    });
  },
  methods: {
    formatCurrency: formatCurrency,
    formatPct: function(value) {
      if (value === null || value === undefined || value === '') return '--';
      return Number(value).toFixed(1) + '%';
    },
    marginStyle: function(pct) {
      if (pct === null || pct === undefined) return {};
      if (Number(pct) < 0) return { color: '#b91c1c', fontWeight: 600 };
      if (Number(pct) < 5) return { color: '#b45309', fontWeight: 600 };
      return {};
    },
    loadPeriods: function() {
      var self = this;
      return getReconProfitabilityPeriods().then(function(data) {
        self.availableMonths = (data && data.months) || [];
      }).catch(function() {
        self.availableMonths = [];
      });
    },
    onTypeChange: function(value) {
      if (this.reconType === value) return;
      this.reconType = value;
      this.loadAll();
    },
    onPeriodTypeChange: function() {
      if (this.periodType === 'month' && !this.selectedMonth && this.monthOptions.length > 0) {
        this.selectedMonth = this.monthOptions[0].value;
      }
      if (this.periodType === 'quarter' && !this.selectedQuarter && this.quarterOptions.length > 0) {
        this.selectedQuarter = this.quarterOptions[0].value;
      }
      if (this.periodType === 'year' && !this.selectedYear && this.yearOptions.length > 0) {
        this.selectedYear = this.yearOptions[0].value;
      }
      this.loadAll();
    },
    buildPeriodParams: function() {
      var params = {};
      if (this.periodType === 'month' && this.selectedMonth) params.month = this.selectedMonth;
      else if (this.periodType === 'quarter' && this.selectedQuarter) params.quarter = this.selectedQuarter;
      else if (this.periodType === 'year' && this.selectedYear) params.year = this.selectedYear;
      else if (this.periodType === 'all') params.all = 'true';
      else params.months = RELATIVE_WINDOWS[this.periodType] || 6;
      return params;
    },
    toggleZeroCharge: function() {
      this.zeroChargeExpanded = !this.zeroChargeExpanded;
      if (this.zeroChargeExpanded) this.loadZeroCharge();
    },
    loadZeroCharge: function() {
      var self = this;
      self.zeroChargeState = 'loading';
      var params = self.buildPeriodParams();
      if (self.reconType) params.reconType = self.reconType;
      getReconZeroChargeLines(params).then(function(data) {
        self.zeroChargeItems = (data && data.items) || [];
        self.zeroChargeState = 'loaded';
      }).catch(function() {
        self.zeroChargeItems = [];
        self.zeroChargeState = 'loaded';
      });
    },
    loadAll: function() {
      var self = this;
      self.state = 'loading';
      self.error = '';
      var params = self.buildPeriodParams();
      if (self.reconType) params.reconType = self.reconType;
      var clientParams = Object.assign({}, params);
      if (self.search) clientParams.search = self.search;

      Promise.all([
        getReconProfitabilitySummary(params),
        getReconProfitabilityClients(clientParams),
        getReconProfitabilityProducts(params)
      ]).then(function(results) {
        self.summary = results[0];
        self.clients = results[1].items || [];
        self.products = results[2].items || [];
        self.state = 'loaded';
        if (self.zeroChargeExpanded) self.loadZeroCharge();
      }).catch(function(err) {
        self.error = (err && err.message) ? err.message : 'Failed to load profitability data.';
        self.state = 'error';
      });
    }
  }
};
</script>

<style scoped>
.recon-profitability { min-height: 100%; }
.page-body { padding: 24px; }
.card {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
  padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); margin-bottom: 20px;
}
.card h3 { font-size: 0.9375rem; font-weight: 700; color: #0f172a; margin: 0 0 16px 0; }
.overview-filters { display: flex; gap: 16px; align-items: flex-end; flex-wrap: wrap; }
.filter-group { display: flex; flex-direction: column; gap: 4px; }
.filter-label { font-size: 0.75rem; font-weight: 600; color: #64748b; }
.filter-select, .filter-input {
  border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px;
  font-size: 0.875rem; font-family: 'Inter', sans-serif;
}
.apply-btn {
  border: 1px solid #0f172a; background: #0f172a; color: #fff; border-radius: 8px;
  padding: 8px 18px; font-size: 0.875rem; cursor: pointer;
}
.window-label { font-size: 0.875rem; color: #334155; padding: 8px 0; }
.summary-cards { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 20px; }
.summary-cards > * { flex: 1; min-width: 180px; }
.type-tabs { display: flex; gap: 4px; }
.type-tab {
  background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; padding: 8px 20px;
  border-radius: 8px; font-size: 0.8125rem; font-weight: 600; cursor: pointer;
  font-family: 'Inter', sans-serif;
}
.type-tab-active { background: #4f46e5; color: #fff; border-color: #4f46e5; }
.type-summary { margin-bottom: 20px; }
.type-summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
.type-summary-col { border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; }
.type-summary-col h4 {
  font-size: 0.8125rem; font-weight: 700; color: #0f172a;
  margin: 0 0 8px 0; text-transform: uppercase; letter-spacing: 0.04em;
}
.type-summary-combined { background: #f8fafc; border-color: #c7d2fe; }
.summary-row { display: flex; justify-content: space-between; align-items: baseline; padding: 6px 0; font-size: 0.8125rem; color: #334155; }
.summary-row + .summary-row { border-top: 1px solid #f1f5f9; }
.summary-name { color: #64748b; font-weight: 500; }
.summary-value { font-weight: 600; text-align: right; }
.type-no-data { font-size: 0.8125rem; color: #94a3b8; padding: 8px 0; margin: 0; }
@media (max-width: 900px) { .type-summary-grid { grid-template-columns: 1fr; } }
.warn-message {
  background: #fffbeb; border: 1px solid #fde68a; color: #92400e;
  padding: 12px 16px; border-radius: 8px; font-size: 0.875rem; margin-bottom: 20px;
}
.zero-charge-trigger { cursor: pointer; display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.zero-charge-trigger:hover { border-color: #f59e0b; }
.zero-charge-toggle { font-size: 0.75rem; font-weight: 600; white-space: nowrap; }
.zero-charge-detail h3 { font-size: 0.8125rem; }
.data-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 0.8125rem; }
.data-table th { text-align: left; padding: 10px 12px; background: #f1f5f9; color: #475569; font-weight: 700; font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid #e2e8f0; white-space: nowrap; }
.data-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; }
.data-table tbody tr:last-child td { border-bottom: none; }
.no-data { font-size: 0.875rem; color: #94a3b8; text-align: center; padding: 32px 16px; background: #f8fafc; border-radius: 8px; border: 1px dashed #e2e8f0; }
.error-message { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; padding: 16px; border-radius: 8px; font-size: 0.875rem; margin-bottom: 16px; }
.loading-state { color: #64748b; font-size: 0.875rem; padding: 24px; }
</style>
